import axios from 'axios';
import { createAPIClient } from '@/utils/network-utils/createAPIClient';

import Service from '@/services/Service';
import type { NavigationModel } from '@/services/models/Navigation/NavigationModel';
import ServerNavigationService from '@/services/serverless/ServerNavigationService';
import {
  ResourceNotFoundError,
  UnableToAccessResourceError
} from '@/utils/errors';
import type { INavigation } from '@/services/models/Navigation/INavigation';
import { EnvironmentService } from '../EnvironmentService';
import NavigationServiceMock from './NavigationServiceMock';

/**
 * An isomorphic service that retrieves navigation data.
 *
 * If is used on the client, it will make an HTTP request to the server.
 *
 * If is used on the server, it will use the {@link ServerNavigationService} to retrieve the navigation data.
 */
export class NavigationService extends Service {
  private client = createAPIClient({ baseURL: '/api' });

  /**
   * Retrieves the navigation data.
   *
   * @returns A promise that resolves with the navigation data.
   * @throws A {@link ResourceNotFoundError} If no navigation data is found.
   * @throws An {@link UnableToAccessResourceError} If an unexpected error occurs.
   */
  public async getNavigation(): Promise<ReadonlyArray<INavigation>> {
    if ((typeof window === "undefined")) {
      return ServerNavigationService.getNavigation();
    }

    try {
      const { data } =
        await this.client.get<ReadonlyArray<NavigationModel>>('/navigation');

      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new ResourceNotFoundError(`Navigation data not found.`, {
            cause: error
          });
        }
      }

      throw new UnableToAccessResourceError(
        'An unexpected error occurred when retrieving navigation data.',
        { cause: error }
      );
    }
  }
}

export default NavigationService.withMock(
  new NavigationServiceMock(NavigationService)
) as unknown as NavigationService;
